import React, { useState, useEffect } from 'react';
import { FaWallet, FaMoneyBillWave, FaExchangeAlt, FaQrcode, FaArrowUp, FaArrowDown } from 'react-icons/fa';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';
import { FiRefreshCw, FiCopy, FiCheck, FiSearch } from 'react-icons/fi';
import './WalletManagement.css';

interface WalletData {
  usd_balance: number;
  lrd_balance: number;
}

interface TransferData {
  amount: number;
  currency: 'USD' | 'LRD';
  recipient_contact: string;
  notes?: string;
  purpose?: string;
}

interface LookupResult {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  is_active: boolean;
}

interface Transaction {
  id: number;
  reference: string;
  amount: number;
  currency: string;
  status: string;
  description: string;
  purpose: string;
  created_at: string;
  type: 'debit' | 'credit';
  counterparty_name: string;
  counterparty_email: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'https://api.liberianpost.com';

const WalletManagement: React.FC = () => {
  const [wallet, setWallet] = useState<WalletData>({ usd_balance: 0, lrd_balance: 0 });
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'LRD'>('USD');
  const [showTransfer, setShowTransfer] = useState(false);
  const [showQRCode, setShowQRCode] = useState(false);
  const [transferData, setTransferData] = useState<TransferData>({
    amount: 0,
    currency: 'USD',
    recipient_contact: '',
    notes: ''
  });
  const [lookupResult, setLookupResult] = useState<LookupResult | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingTransactions, setFetchingTransactions] = useState(false);
  const [notification, setNotification] = useState<{ show: boolean; message: string; type: string }>({
    show: false,
    message: '',
    type: ''
  });

  const token = localStorage.getItem('merchant_token');

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Fetch wallet balance
  const fetchWalletBalance = async () => {
    try {
      const response = await fetch(`${API_URL}/merchants/wallet`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setWallet({
          usd_balance: data.data.usd_balance || 0,
          lrd_balance: data.data.lrd_balance || 0
        });
      }
    } catch (error) {
      console.error('Error fetching wallet:', error);
    }
  };

  // Fetch transactions
  const fetchTransactions = async () => {
    setFetchingTransactions(true);
    try {
      const response = await fetch(`${API_URL}/merchants/wallet/transactions?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setTransactions(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setFetchingTransactions(false);
    }
  };

  // Lookup recipient
  const handleLookup = async () => {
    if (!transferData.recipient_contact) {
      showNotification('Please enter recipient email or phone number', 'error');
      return;
    }

    setLookupLoading(true);
    setLookupResult(null);

    try {
      const response = await fetch(`${API_URL}/merchants/wallet/lookup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ contact: transferData.recipient_contact })
      });

      const data = await response.json();

      if (data.success) {
        setLookupResult(data.data);
        showNotification(`Recipient found: ${data.data.first_name} ${data.data.last_name}`, 'success');
      } else {
        showNotification(data.error || 'User not found', 'error');
      }
    } catch (error) {
      console.error('Lookup error:', error);
      showNotification('Failed to lookup recipient', 'error');
    } finally {
      setLookupLoading(false);
    }
  };

  // Send money
  const handleSendMoney = async () => {
    if (transferData.amount <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

    const maxAmount = transferData.currency === 'USD' ? wallet.usd_balance : wallet.lrd_balance;
    if (transferData.amount > maxAmount) {
      showNotification(`Insufficient ${transferData.currency} balance`, 'error');
      return;
    }

    if (!transferData.recipient_contact) {
      showNotification('Please enter recipient email or phone number', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/merchants/wallet/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_contact: transferData.recipient_contact,
          amount: transferData.amount,
          currency: transferData.currency,
          description: transferData.notes,
          purpose: transferData.purpose || 'Merchant transfer'
        })
      });

      const data = await response.json();

      if (data.success) {
        showNotification(`Transfer of ${transferData.currency} ${transferData.amount.toLocaleString()} completed successfully!`, 'success');
        setShowTransfer(false);
        setTransferData({ amount: 0, currency: 'USD', recipient_contact: '', notes: '' });
        setLookupResult(null);
        fetchWalletBalance();
        fetchTransactions();
      } else {
        showNotification(data.error || 'Transfer failed', 'error');
      }
    } catch (error) {
      console.error('Send money error:', error);
      showNotification('Failed to send money', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyWalletId = async () => {
    const walletId = `MERCHANT-${localStorage.getItem('merchant_data') ? JSON.parse(localStorage.getItem('merchant_data')!).merchant_id : '000'}`;
    await navigator.clipboard.writeText(walletId);
    setCopied(true);
    showNotification('Wallet ID copied to clipboard', 'success');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatCurrency = (amount: number, currency: 'USD' | 'LRD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  useEffect(() => {
    fetchWalletBalance();
    fetchTransactions();
  }, []);

  return (
    <div className="wallet-management">
      {notification.show && (
        <div className={`wallet-notification ${notification.type}`}>
          {notification.type === 'success' && <FiCheck />}
          <span>{notification.message}</span>
        </div>
      )}

      {showQRCode && (
        <div className="qr-modal-overlay" onClick={() => setShowQRCode(false)}>
          <div className="qr-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Your Wallet ID</h3>
            <div className="qr-code-display">
              <div className="qr-placeholder">
                <FaWallet size={80} />
                <p>Merchant Wallet</p>
              </div>
            </div>
            <div className="wallet-address">
              <p>Merchant ID: {JSON.parse(localStorage.getItem('merchant_data') || '{}').merchant_id}</p>
              <button onClick={handleCopyWalletId}>
                {copied ? <FiCheck /> : <FiCopy />}
              </button>
            </div>
            <button className="close-qr" onClick={() => setShowQRCode(false)}>Close</button>
          </div>
        </div>
      )}

      {showTransfer && (
        <div className="transfer-modal-overlay" onClick={() => setShowTransfer(false)}>
          <div className="transfer-modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Send Money</h3>
            <div className="transfer-form">
              <div className="form-group">
                <label>Currency</label>
                <div className="currency-selector">
                  <button
                    className={transferData.currency === 'USD' ? 'active' : ''}
                    onClick={() => setTransferData({ ...transferData, currency: 'USD' })}
                  >
                    USD
                  </button>
                  <button
                    className={transferData.currency === 'LRD' ? 'active' : ''}
                    onClick={() => setTransferData({ ...transferData, currency: 'LRD' })}
                  >
                    LRD
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Amount</label>
                <div className="amount-input">
                  <span>{transferData.currency === 'USD' ? '$' : 'L$'}</span>
                  <input
                    type="number"
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: parseFloat(e.target.value) || 0 })}
                    placeholder="0.00"
                  />
                </div>
                <small>Available: {formatCurrency(
                  transferData.currency === 'USD' ? wallet.usd_balance : wallet.lrd_balance,
                  transferData.currency
                )}</small>
              </div>

              <div className="form-group">
                <label>Recipient Email or Phone Number</label>
                <div className="lookup-input">
                  <input
                    type="text"
                    value={transferData.recipient_contact}
                    onChange={(e) => {
                      setTransferData({ ...transferData, recipient_contact: e.target.value });
                      setLookupResult(null);
                    }}
                    placeholder="email@example.com or phone number"
                  />
                  <button onClick={handleLookup} disabled={lookupLoading} className="lookup-btn">
                    {lookupLoading ? '...' : <FiSearch />}
                  </button>
                </div>
              </div>

              {lookupResult && (
                <div className="lookup-result">
                  <div className="lookup-avatar">
                    {lookupResult.first_name?.charAt(0)}{lookupResult.last_name?.charAt(0)}
                  </div>
                  <div className="lookup-info">
                    <p className="lookup-name">{lookupResult.first_name} {lookupResult.last_name}</p>
                    <p className="lookup-email">{lookupResult.email}</p>
                    <p className="lookup-phone">{lookupResult.phone}</p>
                  </div>
                  {lookupResult.is_active && <div className="lookup-status active">Active</div>}
                </div>
              )}

              <div className="form-group">
                <label>Note (Optional)</label>
                <textarea
                  value={transferData.notes}
                  onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                  placeholder="Add a note..."
                  rows={3}
                />
              </div>

              <div className="transfer-actions">
                <button className="cancel-btn" onClick={() => {
                  setShowTransfer(false);
                  setLookupResult(null);
                  setTransferData({ amount: 0, currency: 'USD', recipient_contact: '', notes: '' });
                }}>Cancel</button>
                <button className="send-btn" onClick={handleSendMoney} disabled={loading || !lookupResult}>
                  {loading ? 'Processing...' : `Send ${transferData.currency}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="wallet-header">
        <h2>
          <FaWallet /> My Wallet
        </h2>
        <div className="wallet-actions">
          <button onClick={() => { fetchWalletBalance(); fetchTransactions(); }} className="refresh-wallet-btn">
            <FiRefreshCw />
          </button>
          <div className="currency-toggle">
            <button
              className={selectedCurrency === 'USD' ? 'active' : ''}
              onClick={() => setSelectedCurrency('USD')}
            >
              USD
            </button>
            <button
              className={selectedCurrency === 'LRD' ? 'active' : ''}
              onClick={() => setSelectedCurrency('LRD')}
            >
              LRD
            </button>
          </div>
        </div>
      </div>

      <div className="wallet-balances">
        <div className={`balance-card usd ${selectedCurrency === 'USD' ? 'active' : ''}`}>
          <div className="balance-icon">
            <HiOutlineCurrencyDollar />
          </div>
          <div className="balance-info">
            <span className="balance-label">USD Balance</span>
            <h2>{formatCurrency(wallet.usd_balance, 'USD')}</h2>
            <span className="balance-subtitle">Available for withdrawal</span>
          </div>
          <div className="balance-actions">
            <button onClick={() => setShowTransfer(true)}>
              <FaExchangeAlt /> Send
            </button>
            <button onClick={() => setShowQRCode(true)}>
              <FaQrcode /> Receive
            </button>
          </div>
        </div>

        <div className={`balance-card lrd ${selectedCurrency === 'LRD' ? 'active' : ''}`}>
          <div className="balance-icon">
            <FaMoneyBillWave />
          </div>
          <div className="balance-info">
            <span className="balance-label">LRD Balance</span>
            <h2>{formatCurrency(wallet.lrd_balance, 'LRD')}</h2>
            <span className="balance-subtitle">Available for withdrawal</span>
          </div>
          <div className="balance-actions">
            <button onClick={() => setShowTransfer(true)}>
              <FaExchangeAlt /> Send
            </button>
            <button onClick={() => setShowQRCode(true)}>
              <FaQrcode /> Receive
            </button>
          </div>
        </div>
      </div>

      <div className="recent-wallet-activities">
        <h3>Recent Activity</h3>
        {fetchingTransactions ? (
          <div className="loading-transactions">Loading transactions...</div>
        ) : transactions.length > 0 ? (
          <div className="activity-list">
            {transactions.map((tx) => (
              <div key={tx.id} className="activity-item">
                <div className={`activity-icon ${tx.type === 'credit' ? 'incoming' : 'outgoing'}`}>
                  {tx.type === 'credit' ? <FaArrowDown /> : <FaArrowUp />}
                </div>
                <div className="activity-details">
                  <p className="activity-title">
                    {tx.type === 'credit' ? 'Received from' : 'Sent to'} {tx.counterparty_name}
                  </p>
                  <p className="activity-date">{formatDate(tx.created_at)}</p>
                </div>
                <div className={`activity-amount ${tx.type === 'credit' ? 'positive' : 'negative'}`}>
                  {tx.type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount, tx.currency as 'USD' | 'LRD')}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-transactions">
            <p>No transactions yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletManagement;
